import { Button, Icon } from "@legal-500/legal500-design-system";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  pendingCompaniesService,
  type PendingCompany,
  type CompanyReviewEvent,
} from "../../../api/pendingCompanies.service";
import { formatDateTime } from "../../../utils";
import ReviewForm from "./ReviewForm";
import { EntryActions } from "./EntryActions";
import { ResolvedCompany } from "./ResolvedCompany";
import { ReviewStatusBadge } from "./ReviewStatusBadge";
import { CompanyHistory } from "../CompanyHistory";
import { RecordDetailsModal } from "../components/RecordDetailsModal";
import styles from "./Review.module.css";

export type ReviewAction = "edit" | "approve" | "reject" | "correct" | "relink";
export default function ReviewPanel({
  id,
  canWrite,
  onClose,
  onRefresh,
}: {
  id: string;
  canWrite: boolean;
  onClose: () => void;
  onRefresh: () => void;
}) {
  const [record, setRecord] = useState<PendingCompany | null>(null);
  const [events, setEvents] = useState<CompanyReviewEvent[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState("");
  const [historyRetry, setHistoryRetry] = useState(0);
  const [action, setAction] = useState<ReviewAction | null>(null);
  const [busy, setBusy] = useState(false);
  const content = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    pendingCompaniesService
      .detail(id)
      .then(detail => {
        if (active) setRecord(detail.data);
      })
      .catch(caught => {
        if (active) setError(caught instanceof Error ? caught.message : "Unable to load review.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, revision]);
  useEffect(() => {
    let active = true;
    setHistoryLoading(true);
    setHistoryError("");
    pendingCompaniesService
      .audit(id, 10, (page - 1) * 10)
      .then(history => {
        if (!active) return;
        setEvents(history.data);
        setTotal(history.meta.total);
      })
      .catch(caught => {
        if (active)
          setHistoryError(caught instanceof Error ? caught.message : "Unable to load history.");
      })
      .finally(() => {
        if (active) setHistoryLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, page, revision, historyRetry]);
  const refresh = useCallback(() => {
    setLoading(true);
    setRevision(value => value + 1);
    onRefresh();
  }, [onRefresh]);
  const actionForm =
    record && action && canWrite ? (
      <ReviewForm
        key={action}
        record={record}
        action={action}
        inline={action === "edit" || action === "correct"}
        onBusy={setBusy}
        onCancel={() => {
          const label =
            action === "edit" ? "Edit name" : action === "correct" ? "Edit company name" : null;
          setAction(null);
          requestAnimationFrame(() =>
            content.current
              ?.querySelector<HTMLElement>(
                label ? `button[aria-label="${label}"]` : 'button[aria-haspopup="menu"]',
              )
              ?.focus(),
          );
        }}
        onRefresh={refresh}
        onSuccess={() => {
          setAction(null);
          setPage(1);
          refresh();
        }}
      />
    ) : null;
  const controlsReady = canWrite && !action && !loading && !error;
  return (
    <RecordDetailsModal
      title="Entry Details"
      contentRef={content}
      onClose={() => {
        if (!busy) onClose();
      }}
      notice={
        <>
          {error && (
            <div role="alert">
              {error}{" "}
              <Button
                color="primary"
                className={styles.brandButton}
                variant="outlined"
                onClick={refresh}
              >
                Retry review
              </Button>
            </div>
          )}
          {loading && <p role="status">Loading review…</p>}
        </>
      }
      details={
        record && (
          <>
            <dl className={styles.facts}>
              <dt>Status</dt>
              <dd>
                <ReviewStatusBadge status={record.status} />
              </dd>
              <dt>Name</dt>
              <dd className={styles.editableName}>
                {action === "edit" ? actionForm : <span>{record.name}</span>}
                {record.status === "pending" && controlsReady && (
                  <Button
                    color="primary"
                    className={styles.brandButton}
                    variant="outlined"
                    size="small"
                    aria-label="Edit name"
                    onClick={() => setAction("edit")}
                  >
                    <Icon icon="edit" size="small" />
                    Edit
                  </Button>
                )}
              </dd>
              <dt>Resolved company</dt>
              <dd className={styles.editableName}>
                {action === "correct" ? actionForm : <ResolvedCompany record={record} />}
                {record.status === "approved" &&
                  record.companyId &&
                  record.resolvedCompanyName &&
                  controlsReady && (
                    <Button
                      color="primary"
                      className={styles.brandButton}
                      variant="outlined"
                      size="small"
                      aria-label="Edit company name"
                      onClick={() => setAction("correct")}
                    >
                      <Icon icon="edit" size="small" />
                      Edit
                    </Button>
                  )}
              </dd>
              <dt>Submitted</dt>
              <dd>{formatDateTime(record.createdAt)}</dd>
              <dt>User ID</dt>
              <dd>{record.userId}</dd>
              <dt>ID</dt>
              <dd>{record.id}</dd>
              {record.companyId && (
                <>
                  <dt>Company ID</dt>
                  <dd>{record.companyId}</dd>
                </>
              )}
            </dl>
            {action && action !== "edit" && action !== "correct" && actionForm}
          </>
        )
      }
      history={
        <>
          {historyLoading && <p role="status">Loading history…</p>}
          {historyError && (
            <div role="alert">
              {historyError}{" "}
              <Button
                color="primary"
                className={styles.brandButton}
                variant="outlined"
                onClick={() => setHistoryRetry(value => value + 1)}
              >
                Retry history
              </Button>
            </div>
          )}
          {!historyLoading && !historyError && (
            <CompanyHistory
              events={events}
              page={page}
              total={total}
              disabled={busy}
              onPageChange={setPage}
            />
          )}
        </>
      }
      footer={
        record && controlsReady && <EntryActions status={record.status} onSelect={setAction} />
      }
    />
  );
}
